import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Lock, Check, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Input } from "@/components/ui/input";
import { Select } from "@/pages/Projects";
import { toast } from "sonner";
import { MARKETING } from "@/lib/marketingContent";

const SECTORS = [
  ["", "Not sure yet — I'll choose later"],
  ["healthcare", "Healthcare / NHS"],
  ["public_sector", "Public sector"],
  ["construction", "Construction"],
  ["it", "IT / Technology"],
  ["financial_services", "Financial services"],
  ["education", "Education"],
  ["manufacturing", "Manufacturing"],
  ["energy_utilities", "Energy / Utilities"],
  ["transport", "Transport / Infrastructure"],
  ["life_sciences", "Pharma / Life sciences"],
  ["defence", "Defence / Aerospace"],
  ["telecoms", "Telecoms"],
  ["retail_hospitality", "Retail / Hospitality"],
  ["professional_services", "Professional services"],
].map(([value, label]) => ({ value, label }));

export default function Signup() {
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const { refresh } = useBranding();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ org_name: "", sector: "", name: "", email: "", password: "" });
  const [sample, setSample] = useState(true);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const next = (e) => {
    e.preventDefault();
    if (form.org_name.trim().length < 2) return toast.error("Give your workspace a name");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Use at least 8 characters for your password");
    setBusy(true);
    try {
      const { data } = await api.post("/public/signup", {
        org_name: form.org_name.trim(),
        sector: form.sector || null,
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        sample_data: sample,
      });
      await adopt(data);
      await refresh().catch(() => {});
      toast.success(`Workspace created — ${data.trial_days} days of full access`);
      if (data.sample_data) {
        toast.info("Building your sample portfolio — it will appear on your dashboard in a few seconds.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not create your workspace");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="marketing-root flex min-h-screen flex-col bg-[color:var(--marketing-bg)] text-[color:var(--marketing-text)]">
      <header className="border-b border-[color:var(--marketing-border)] bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" className="font-marketing-display text-lg font-black tracking-tight" data-testid="signup-logo">
            {MARKETING.product}
          </Link>
          <Link to="/login" data-testid="signup-signin-link" className="text-sm font-semibold transition-colors hover:text-[color:var(--marketing-primary)]">
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md" data-testid="signup-page">
          <p className="overline-label">Step {step} of 2</p>
          <h1 className="mt-4 font-marketing-display text-3xl font-black tracking-tight sm:text-4xl">
            {step === 1 ? "Create your workspace" : "Create your admin account"}
          </h1>
          <p className="mt-3 text-sm text-[color:var(--marketing-text-muted)]">
            {step === 1
              ? "Seven days of full Professional access. Choose a sector and we will configure your stage gates, risk types and terminology for you."
              : "You will be the administrator of this workspace and can invite your team straight away."}
          </p>

          {step === 1 ? (
            <form onSubmit={next} className="mt-8 space-y-5" data-testid="signup-step-1">
              <label className="block">
                <span className="overline-label">Organisation or workspace name</span>
                <Input data-testid="signup-org-input" required value={form.org_name} onChange={set("org_name")}
                  placeholder="e.g. Northside Trust PMO" />
              </label>
              <div>
                <span className="overline-label">Your sector</span>
                <Select testId="signup-sector-select" value={form.sector}
                  onChange={(v) => setForm({ ...form, sector: v })} options={SECTORS} placeholder="Select sector" />
              </div>
              <div>
                <span className="overline-label">How would you like to start?</span>
                <div className="mt-2 space-y-2">
                  {[
                    { value: true, title: "With a sample portfolio", testId: "signup-sample-yes",
                      body: "10 fully worked projects for your sector — RAID, plans, milestones, status reports and dashboards populated from day one. Removable in one click." },
                    { value: false, title: "Empty workspace", testId: "signup-sample-no",
                      body: "Start clean and add your own projects. You can load the sample data later from Admin." },
                  ].map((opt) => (
                    <button
                      key={opt.testId}
                      type="button"
                      data-testid={opt.testId}
                      onClick={() => setSample(opt.value)}
                      className={`w-full rounded-sm border p-3 text-left transition-colors ${
                        sample === opt.value
                          ? "border-[color:var(--marketing-primary)] bg-[color:var(--marketing-primary)]/5"
                          : "border-[color:var(--marketing-border)] hover:border-slate-400"
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        {sample === opt.value && <Check size={13} strokeWidth={2.5} className="text-[color:var(--marketing-primary)]" />}
                        {opt.title}
                      </span>
                      <span className="mt-1 block text-xs text-[color:var(--marketing-text-muted)]">{opt.body}</span>
                    </button>
                  ))}
                </div>
              </div>
              <button data-testid="signup-next-btn" type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-[color:var(--marketing-primary)] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)]">
                Continue <ArrowRight size={15} strokeWidth={1.5} />
              </button>
            </form>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="signup-step-2">
              <label className="block">
                <span className="overline-label">Your full name</span>
                <Input data-testid="signup-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Okafor" />
              </label>
              <label className="block">
                <span className="overline-label">Work email</span>
                <Input data-testid="signup-email-input" type="email" required value={form.email} onChange={set("email")}
                  placeholder="you@organisation.gov.uk" />
              </label>
              <label className="block">
                <span className="overline-label">Password</span>
                <Input data-testid="signup-password-input" type="password" required value={form.password} onChange={set("password")}
                  placeholder="At least 8 characters" />
              </label>
              <button data-testid="signup-submit-btn" type="submit" disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-[color:var(--marketing-primary)] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)] disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} strokeWidth={1.5} />}
                Start my 7-day trial
              </button>
              <p className="text-center text-[11px] leading-relaxed text-slate-500">
                By creating a workspace you agree to our{" "}
                <Link to="/legal/terms" data-testid="signup-terms-link" className="font-semibold underline hover:text-[color:var(--marketing-primary)]">Terms of Use</Link>{" "}
                and{" "}
                <Link to="/legal/privacy" data-testid="signup-privacy-link" className="font-semibold underline hover:text-[color:var(--marketing-primary)]">Privacy &amp; Data Usage Statement</Link>.
              </p>
              <button type="button" data-testid="signup-back-btn" onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-slate-900">
                <ArrowLeft size={12} strokeWidth={1.5} /> Back
              </button>
            </form>
          )}

          <div className="mt-8 border-t border-[color:var(--marketing-border)] pt-5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <Lock size={12} strokeWidth={1.5} /> 7-day full access. No card required.
            </p>
            <ul className="mt-3 space-y-1.5">
              {["Nothing is charged and nothing auto-renews",
                "Your workspace stays intact if you do not continue",
                "Invite your whole team during the trial"].map((t) => (
                <li key={t} className="flex gap-2 text-xs text-[color:var(--marketing-text-muted)]">
                  <Check size={12} strokeWidth={2} className="mt-0.5 shrink-0 text-[color:var(--marketing-primary)]" />{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
